import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { fehlerText } from "@/lib/fehler";
import {
  Loader2, ArrowLeft, Pencil, Clock, Receipt, FileText, MapPin, Phone, Mail,
  MessageCircle, FileSignature, UtensilsCrossed, CheckCircle2, ClipboardList, Wallet,
} from "lucide-react";
import { formatDateDE, dateRangeDE, toYMDLocal } from "@/lib/utils";
import { tagesart } from "@/lib/tagesart";
import { waOeffnen, rechnungWaText } from "@/lib/wa";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const num = (v, d = 2) => Number(v || 0).toLocaleString("de-DE", { minimumFractionDigits: d, maximumFractionDigits: d });
const MONTHS = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August",
  "September", "Oktober", "November", "Dezember"];
const monthLabel = (ym) => {
  const [y, m] = (ym || "").split("-");
  return m ? `${MONTHS[Number(m) - 1]} ${y}` : "—";
};

const BigAction = ({ icon: Icon, title, hint, onClick, testId }) => (
  <button onClick={onClick} data-testid={testId}
    className="group text-left border border-[#BA6D52] bg-[#FAF5F1] hover:bg-[#BA6D52] p-5 transition-colors">
    <Icon size={20} className="text-[#BA6D52] group-hover:text-white transition-colors" />
    <p className="font-serif text-lg text-[#2C2A26] group-hover:text-white mt-3 transition-colors">{title}</p>
    <p className="text-xs text-[#757067] group-hover:text-white/80 mt-1 transition-colors">{hint}</p>
  </button>
);

const SmallAction = ({ icon: Icon, children, onClick, testId }) => (
  <button onClick={onClick} data-testid={testId}
    className="inline-flex items-center gap-2 border border-[#E3DECE] bg-white hover:border-[#BA6D52] hover:text-[#BA6D52] px-4 py-2.5 text-[10px] uppercase tracking-[0.15em] transition-colors">
    <Icon size={13} /> {children}
  </button>
);

const Block = ({ icon: Icon, title, count, children }) => (
  <div>
    <p className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#BA6D52] mb-3">
      <Icon size={13} /> {title}{count != null && ` (${count})`}
    </p>
    {children}
  </div>
);

export default function KundeDetail({ pwd, kunde, onBack, onEdit, onCreateInvoice, onRecordHours, onOpenTab }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [menus, setMenus] = useState([]);
  const [karteien, setKarteien] = useState([]);

  const kundeAdresse = [kunde.strasse, [kunde.plz, kunde.ort].filter(Boolean).join(" ")]
    .filter(Boolean).join(", ");

  const loadKarteien = useCallback(async () => {
    try {
      const { data: k } = await axios.get(`${API}/admin/karteien`, {
        params: { kunde_id: kunde.id }, headers: { "X-Admin-Password": pwd },
      });
      setKarteien(k || []);
    } catch { /* Karteien sind optional */ }
  }, [kunde.id, pwd]);

  useEffect(() => { loadKarteien(); }, [loadKarteien]);

  const karteiAnlegen = () => onOpenTab?.("karteien", {
    neu: {
      kunde_id: kunde.id,
      kunde: kunde.betrieb,
      ort: kundeAdresse,
      titel: "",
    },
  });

  const loadMenus = useCallback(async () => {
    try {
      const { data: m } = await axios.get(`${API}/admin/menuekarten`, {
        params: { kunde_id: kunde.id }, headers: { "X-Admin-Password": pwd },
      });
      setMenus(m || []);
    } catch { /* Menüs sind optional */ }
  }, [kunde.id, pwd]);

  useEffect(() => { loadMenus(); }, [loadMenus]);

  const load = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/admin/kunden/${kunde.id}/akte`, {
        headers: { "X-Admin-Password": pwd },
      });
      setData(d);
    } catch {
      toast.error("Kundenakte konnte nicht geladen werden");
    } finally {
      setLoading(false);
    }
  }, [kunde.id, pwd]);

  useEffect(() => { load(); }, [load]);

  const [anzahlungen, setAnzahlungen] = useState([]);
  const [anzForm, setAnzForm] = useState(null);
  const [anzBusy, setAnzBusy] = useState(false);

  const loadAnzahlungen = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/admin/anzahlungen`, {
        headers: { "X-Admin-Password": pwd },
      });
      const key = (kunde.betrieb || "").trim().toLowerCase();
      setAnzahlungen((d || []).filter((a) => (a.betrieb || "").trim().toLowerCase() === key));
    } catch { /* Anzahlungen sind optional */ }
  }, [kunde.betrieb, pwd]);

  useEffect(() => { loadAnzahlungen(); }, [loadAnzahlungen]);

  const anzahlungAnlegen = async () => {
    const brutto = Number(String(anzForm.betrag_brutto).replace(",", "."));
    if (!brutto || brutto <= 0) { toast.error("Bitte einen Betrag eintragen"); return; }
    setAnzBusy(true);
    try {
      await axios.post(`${API}/admin/anzahlungen`, {
        betrieb: kunde.betrieb, ansprechpartner: kunde.ansprechpartner || "",
        email: kunde.email || "", betrag_brutto: brutto,
        mwst_prozent: Number(anzForm.mwst_prozent) || 19,
        zweck: anzForm.zweck || "Anzahlung für kommende Einsätze",
        zahlart: anzForm.zahlart, erhalten_am: anzForm.erhalten_am, notiz: anzForm.notiz || "",
      }, { headers: { "X-Admin-Password": pwd } });
      toast.success("Anzahlung hinterlegt");
      setAnzForm(null);
      await Promise.all([loadAnzahlungen(), load()]);
    } catch (err) {
      toast.error(fehlerText(err, "Anzahlung konnte nicht gespeichert werden"));
    } finally { setAnzBusy(false); }
  };

  const anzahlungPdf = async (a) => {
    try {
      const { data: blob } = await axios.get(`${API}/admin/anzahlungen/${a.id}/pdf`, {
        headers: { "X-Admin-Password": pwd }, responseType: "blob",
      });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) { toast.error(fehlerText(err, "PDF konnte nicht geöffnet werden")); }
  };

  const anzahlungSenden = async (a) => {
    try {
      await axios.post(`${API}/admin/anzahlungen/${a.id}/senden`, {},
        { headers: { "X-Admin-Password": pwd } });
      toast.success("Anzahlungsrechnung versendet");
    } catch (err) { toast.error(fehlerText(err, "Versand fehlgeschlagen")); }
  };

  const months = useMemo(() => {
    const map = new Map();
    for (const s of data?.shifts || []) {
      const ym = (s.datum || "").slice(0, 7);
      if (!map.has(ym)) map.set(ym, { ym, items: [], hours: 0, net: 0, open: 0 });
      const g = map.get(ym);
      g.items.push(s);
      g.hours += Number(s.hours || 0);
      g.net += Number(s.net_eur || 0);
      if (s.confirmed_at && !s.invoiced_at) g.open += 1;
    }
    for (const g of map.values()) {
      g.items.sort((a, b) => (a.datum || "").localeCompare(b.datum || ""));
    }
    return [...map.values()].sort((a, b) => b.ym.localeCompare(a.ym));
  }, [data]);

  const billable = (g) => g.items.filter((s) => s.confirmed_at && !s.invoiced_at);

  const sammelrechnung = (g) => {
    const use = billable(g).slice().sort((a, b) => (a.datum || "").localeCompare(b.datum || ""));
    if (!use.length) { toast.error("Keine bestätigten, offenen Stunden in diesem Monat"); return; }
    const first = use[0].datum;
    const last = use[use.length - 1].datum;
    const zeitraum = dateRangeDE(first, last);
    onCreateInvoice?.({
      form: {
        betrieb: kunde.betrieb,
        ansprechpartner: kunde.ansprechpartner || "",
        email: kunde.rechnung_email || kunde.email || "",
        telefon: kunde.telefon || "",
        ort: [kunde.strasse, [kunde.plz, kunde.ort].filter(Boolean).join(" ")].filter(Boolean).join(", "),
        einsatzdatum: last,
        leistungszeitraum: zeitraum,
        notes: `Sammelrechnung ${monthLabel(g.ym)} — ${use.length} Einsätze (${zeitraum}), ${num(g.hours)} Stunden.`,
        admin_notes: `Stundenkonto ${monthLabel(g.ym)} · Nachweise: ${use.map((s) => s.nachweis_number).sort().join(", ")}`,
      },
      lineItems: use.map((s) => {
        const t = tagesart(s.datum);
        return {
          type_key: t.we ? "mietkoch_we" : "mietkoch_werktag",
          name: `Mietkoch-Stunden ${t.tag}, ${formatDateDE(s.datum)}`,
          description: `${s.von}–${s.bis} Uhr · ${t.art}${t.art !== "Werktag" ? "s-Satz" : ""} · bestätigt durch ${s.confirmed_by}`,
          quantity: Number(s.hours || 0),
          unit_name: "Stunde",
          unit_price_net: Number(s.stundensatz_eur || 0),
          tax_rate: 19,
        };
      }),
      shiftIds: use.map((s) => s.id),
    });
  };

  const alleOffenen = () => {
    const use = (data?.shifts || []).filter((s) => s.confirmed_at && !s.invoiced_at)
      .slice().sort((a, b) => (a.datum || "").localeCompare(b.datum || ""));
    if (!use.length) { toast.error("Keine offenen bestätigten Stunden"); return; }
    sammelrechnung({ ym: (use[0].datum || "").slice(0, 7), items: use,
      hours: use.reduce((a, s) => a + Number(s.hours || 0), 0), net: 0, open: use.length });
  };

  const leereRechnung = () => {
    onCreateInvoice?.({
      form: {
        betrieb: kunde.betrieb,
        ansprechpartner: kunde.ansprechpartner || "",
        email: kunde.rechnung_email || kunde.email || "",
        telefon: kunde.telefon || "",
        ort: [kunde.strasse, [kunde.plz, kunde.ort].filter(Boolean).join(" ")].filter(Boolean).join(", "),
        einsatzdatum: toYMDLocal(),
        leistungszeitraum: "",
        notes: "", admin_notes: "",
      },
      lineItems: [{
        type_key: "mietkoch_werktag", name: "Mietkoch-Stunden", description: "",
        quantity: 1, unit_name: "Stunde", unit_price_net: Number(kunde.stundensatz_eur || 0), tax_rate: 19,
      }],
    });
  };

  const s = data?.summary;
  const openShifts = (data?.shifts || []).filter((x) => x.confirmed_at && !x.invoiced_at);
  const offene = data?.offene_rechnungen || [];
  const [busyId, setBusyId] = useState("");

  const linkHolen = async (o) => {
    if (o.url) return o.url;
    if (o.art !== "direct") return "";
    const { data: l } = await axios.post(`${API}/admin/direct-invoices/${o.id}/public-link`, {},
      { headers: { "X-Admin-Password": pwd } });
    return l.url;
  };

  const rechnungAktion = async (o, aktion) => {
    setBusyId(o.id + aktion);
    try {
      if (aktion === "seite" || aktion === "whatsapp") {
        const url = await linkHolen(o);
        if (!url) { toast.error("Keine Rechnungsseite verfügbar"); return; }
        navigator.clipboard?.writeText(url);
        if (aktion === "seite") {
          window.open(url, "_blank", "noopener");
          toast.success("Rechnungsseite geöffnet — Link ist kopiert");
        } else {
          const nr = waOeffnen(o.telefon || kunde.telefon, rechnungWaText({
            ansprechpartner: o.ansprechpartner || kunde.ansprechpartner || "",
            nummer: o.nummer, url,
          }));
          toast.success(nr ? "WhatsApp geöffnet — Nachricht ist vorbereitet"
            : "WhatsApp geöffnet — Kontakt noch wählen, Link ist kopiert", { duration: 7000 });
        }
      } else if (aktion === "mail") {
        const ziel = kunde.rechnung_email || o.email || kunde.email || "";
        if (!window.confirm(`Rechnung ${o.nummer} erneut per E-Mail an ${ziel} senden?`)) return;
        const { data: res } = o.art === "direct"
          ? await axios.post(`${API}/admin/direct-invoices/${o.id}/resend-mail`, { email: ziel },
              { headers: { "X-Admin-Password": pwd } })
          : await axios.post(`${API}/admin/angebotsseiten/${o.id}/rechnung/send-email`, { email: ziel },
              { headers: { "X-Admin-Password": pwd } });
        toast.success(`Rechnung erneut gesendet an ${res.mail_to || ziel}`, { duration: 7000 });
        load();
      } else if (aktion === "bezahlt") {
        if (!window.confirm(`Zahlung für ${o.nummer} (${num(o.brutto_eur)} €) als erhalten markieren?`)) return;
        if (o.art === "direct") {
          await axios.post(`${API}/admin/direct-invoices/${o.id}/mark-paid`, { notify_customer: true },
            { headers: { "X-Admin-Password": pwd } });
        } else {
          await axios.post(`${API}/admin/angebotsseiten/${o.id}/rechnung/bezahlt?bezahlt=true`, {},
            { headers: { "X-Admin-Password": pwd } });
        }
        toast.success("Als bezahlt markiert");
        load();
      }
    } catch (err) {
      toast.error("Fehler: " + fehlerText(err));
    } finally {
      setBusyId("");
    }
  };

  return (
    <div data-testid="kunde-detail" className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button onClick={onBack} data-testid="kunde-detail-back"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#757067] hover:text-[#BA6D52] transition-colors">
          <ArrowLeft size={14} /> Alle Kunden
        </button>
        <button onClick={() => onEdit?.(kunde)} data-testid="kunde-detail-edit"
          className="inline-flex items-center gap-2 border border-[#E3DECE] hover:border-[#BA6D52] hover:text-[#BA6D52] px-4 py-2.5 text-[10px] uppercase tracking-[0.15em] transition-colors">
          <Pencil size={13} /> Stammdaten bearbeiten
        </button>
      </div>

      {/* Kopf */}
      <div className="border border-[#E3DECE] bg-white p-6 md:p-8">
        <h2 className="font-serif text-3xl sm:text-4xl font-light tracking-tight text-[#2C2A26]">{kunde.betrieb}</h2>
        {kunde.ansprechpartner && <p className="text-sm text-[#757067] mt-1">{kunde.ansprechpartner}</p>}

        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-2 text-sm">
          {(kunde.strasse || kunde.ort) && (
            <p className="inline-flex items-center gap-2 text-[#2C2A26]">
              <MapPin size={13} className="text-[#BA6D52]" />
              {[kunde.strasse, [kunde.plz, kunde.ort].filter(Boolean).join(" ")].filter(Boolean).join(", ")}
            </p>
          )}
          {kunde.telefon && (
            <p className="inline-flex items-center gap-2 text-[#2C2A26]">
              <Phone size={13} className="text-[#BA6D52]" />
              <a href={`tel:${kunde.telefon}`} className="hover:text-[#BA6D52]">{kunde.telefon}</a>
              <a href={`https://wa.me/${kunde.telefon.replace(/[^\d]/g, "").replace(/^0/, "49")}`}
                target="_blank" rel="noopener noreferrer" data-testid="kunde-detail-whatsapp"
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.1em] text-[#25D366] hover:underline">
                <MessageCircle size={12} /> WhatsApp
              </a>
            </p>
          )}
          {kunde.email && (
            <p className="inline-flex items-center gap-2 text-[#2C2A26]">
              <Mail size={13} className="text-[#BA6D52]" />
              <a href={`mailto:${kunde.email}`} className="hover:text-[#BA6D52]">{kunde.email}</a>
            </p>
          )}
          {kunde.rechnung_email && (
            <p className="inline-flex items-center gap-2 text-[#2C2A26]" data-testid="kunde-rechnung-email">
              <Receipt size={13} className="text-[#BA6D52]" />
              <span className="text-[#757067]">Rechnungen an:</span>
              <a href={`mailto:${kunde.rechnung_email}`} className="hover:text-[#BA6D52]">{kunde.rechnung_email}</a>
            </p>
          )}
          {kunde.stundensatz_eur > 0 && (
            <p className="text-[#2C2A26]">
              <span className="text-[#757067]">Vereinbarter Stundensatz: </span>
              <b className="text-[#BA6D52]">{num(kunde.stundensatz_eur)} € netto / Stunde</b>
            </p>
          )}
          {kunde.ust_id && <p className="text-[#757067]">USt-IdNr.: {kunde.ust_id}</p>}
        </div>
        {kunde.notiz && (
          <p className="mt-4 border-l-2 border-[#BA6D52] pl-4 text-sm text-[#757067] italic">{kunde.notiz}</p>
        )}

        {s && (kunde.typ === "privat" ? (
          <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-3">
            <div className="border border-[#E3DECE] p-3">
              <p className="text-[9px] uppercase tracking-[0.2em] text-[#757067]">Veranstaltungen</p>
              <p className="font-serif text-xl mt-1" data-testid="kunde-detail-einsaetze">
                {(data?.angebote || []).length + s.inquiry_count}
              </p>
            </div>
            <div className="border border-[#E3DECE] p-3">
              <p className="text-[9px] uppercase tracking-[0.2em] text-[#757067]">Nächste Veranstaltung</p>
              <p className="font-serif text-xl mt-1">
                {s.naechster_einsatz ? formatDateDE(s.naechster_einsatz) : "—"}
              </p>
            </div>
            <div className="border border-[#E3DECE] p-3">
              <p className="text-[9px] uppercase tracking-[0.2em] text-[#757067]">Umsatz netto gesamt</p>
              <p className="font-serif text-xl mt-1" data-testid="kunde-detail-umsatz">{num(s.umsatz_netto_eur)} €</p>
            </div>
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="border border-[#E3DECE] p-3">
              <p className="text-[9px] uppercase tracking-[0.2em] text-[#757067]">Einsätze</p>
              <p className="font-serif text-xl mt-1" data-testid="kunde-detail-einsaetze">{s.shift_count + s.inquiry_count}</p>
            </div>
            <div className="border border-[#E3DECE] p-3">
              <p className="text-[9px] uppercase tracking-[0.2em] text-[#757067]">Stunden gesamt</p>
              <p className="font-serif text-xl mt-1">{num(s.hours_total)}</p>
            </div>
            <div className="border border-[#BA6D52] bg-[#FAF5F1] p-3">
              <p className="text-[9px] uppercase tracking-[0.2em] text-[#BA6D52]">Offen abzurechnen</p>
              <p className="font-serif text-xl text-[#BA6D52] mt-1" data-testid="kunde-detail-offen">{num(s.hours_open)} Std.</p>
            </div>
            <div className="border border-[#E3DECE] p-3">
              <p className="text-[9px] uppercase tracking-[0.2em] text-[#757067]">Umsatz netto gesamt</p>
              <p className="font-serif text-xl mt-1" data-testid="kunde-detail-umsatz">{num(s.umsatz_netto_eur)} €</p>
            </div>
          </div>
        ))}
        {s?.naechster_einsatz && (
          <p className="text-xs text-[#BA6D52] mt-3">Nächste Veranstaltung: {formatDateDE(s.naechster_einsatz)}</p>
        )}
        {s?.last_einsatz && (
          <p className="text-xs text-[#757067] mt-3">Letzter Einsatz: {formatDateDE(s.last_einsatz)}</p>
        )}
      </div>

      {/* Anzahlungen / Guthaben */}
      <div data-testid="kunde-anzahlungen" className="border border-[#E3DECE] bg-white p-5 md:p-6">
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <p className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">
            <Wallet size={13} /> Anzahlungen
          </p>
          <button onClick={() => setAnzForm({ betrag_brutto: "",
            mwst_prozent: kunde.typ === "privat" ? 7 : 19, zahlart: "bar",
            erhalten_am: new Date().toISOString().slice(0, 10),
            zweck: kunde.typ === "privat" ? "Anzahlung für die kommende Veranstaltung"
              : "Anzahlung für kommende Einsätze", notiz: "" })}
            data-testid="anzahlung-neu"
            className="text-[11px] uppercase tracking-[0.15em] border border-[#E3DECE] hover:border-[#BA6D52] hover:text-[#BA6D52] px-4 py-2 transition-colors">
            Anzahlung hinterlegen
          </button>
        </div>

        {anzahlungen.length > 0 && (
          <>
            <p className="font-serif text-2xl text-[#2C2A26] mt-3" data-testid="anzahlung-guthaben">
              {num(anzahlungen.reduce((s, a) => s + (a.rest_brutto || 0), 0))} € Guthaben
            </p>
            <p className="text-xs text-[#757067]">
              wird automatisch von der nächsten Sammelrechnung abgezogen
            </p>
            <div className="mt-3 divide-y divide-[#EFE9DC] border-t border-[#EFE9DC]">
              {anzahlungen.map((a) => (
                <div key={a.id} data-testid={`anzahlung-${a.nummer}`}
                  className="py-3 flex justify-between items-center gap-3 flex-wrap text-sm">
                  <span>
                    <span className="font-mono text-xs text-[#BA6D52]">{a.nummer}</span>
                    <span className="text-[#757067]">
                      {" · "}{formatDateDE(a.erhalten_am)} · {a.zahlart === "bar" ? "bar erhalten" : "überwiesen"}
                      {a.lexoffice_nummer ? ` · LexOffice ${a.lexoffice_nummer}` : ""}
                    </span>
                  </span>
                  <span className="flex items-center gap-3">
                    <span className="tabular-nums">
                      {num(a.brutto)} €
                      {a.rest_brutto < a.brutto && (
                        <span className="text-[#757067] text-xs"> · noch {num(a.rest_brutto)} €</span>
                      )}
                    </span>
                    <button onClick={() => anzahlungPdf(a)} title="Beleg öffnen"
                      data-testid={`anzahlung-pdf-${a.nummer}`}
                      className="text-[#9A958C] hover:text-[#BA6D52] transition-colors"><FileText size={15} /></button>
                    <button onClick={() => anzahlungSenden(a)} title="Per E-Mail senden"
                      data-testid={`anzahlung-senden-${a.nummer}`}
                      className="text-[#9A958C] hover:text-[#BA6D52] transition-colors"><Mail size={15} /></button>
                  </span>
                </div>
              ))}
            </div>
          </>
        )}

        {anzForm && (
          <div className="mt-4 border border-[#EFE9DC] bg-[#FAFAF7] p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="text-xs text-[#757067]">Betrag brutto (€)
              <input value={anzForm.betrag_brutto} data-testid="anzahlung-betrag" autoFocus
                onChange={(e) => setAnzForm({ ...anzForm, betrag_brutto: e.target.value })}
                placeholder="1200" className="mt-1 w-full bg-white border border-[#E3DECE] px-3 py-2.5 text-sm text-[#2C2A26]" />
            </label>
            <label className="text-xs text-[#757067]">Erhalten am
              <input type="date" value={anzForm.erhalten_am} data-testid="anzahlung-datum"
                onChange={(e) => setAnzForm({ ...anzForm, erhalten_am: e.target.value })}
                className="mt-1 w-full bg-white border border-[#E3DECE] px-3 py-2.5 text-sm text-[#2C2A26]" />
            </label>
            <label className="text-xs text-[#757067]">Zahlungsart
              <select value={anzForm.zahlart} data-testid="anzahlung-zahlart"
                onChange={(e) => setAnzForm({ ...anzForm, zahlart: e.target.value })}
                className="mt-1 w-full bg-white border border-[#E3DECE] px-3 py-2.5 text-sm text-[#2C2A26]">
                <option value="bar">Bar erhalten (gilt als Quittung)</option>
                <option value="ueberweisung">Überweisung</option>
              </select>
            </label>
            <label className="text-xs text-[#757067]">Umsatzsteuer
              <select value={anzForm.mwst_prozent} data-testid="anzahlung-mwst"
                onChange={(e) => setAnzForm({ ...anzForm, mwst_prozent: e.target.value })}
                className="mt-1 w-full bg-white border border-[#E3DECE] px-3 py-2.5 text-sm text-[#2C2A26]">
                <option value="19">19 % (Dienstleistung)</option>
                <option value="7">7 % (Speisen)</option>
              </select>
            </label>
            <label className="text-xs text-[#757067] md:col-span-2">Verwendungszweck
              <input value={anzForm.zweck} data-testid="anzahlung-zweck"
                onChange={(e) => setAnzForm({ ...anzForm, zweck: e.target.value })}
                className="mt-1 w-full bg-white border border-[#E3DECE] px-3 py-2.5 text-sm text-[#2C2A26]" />
            </label>
            <div className="md:col-span-2 flex gap-2">
              <button onClick={anzahlungAnlegen} disabled={anzBusy} data-testid="anzahlung-speichern"
                className="inline-flex items-center gap-2 bg-[#BA6D52] hover:bg-[#9C4F31] disabled:opacity-60 text-white px-5 py-2.5 text-[11px] uppercase tracking-[0.15em] transition-colors">
                {anzBusy && <Loader2 size={13} className="animate-spin" />} Beleg erstellen
              </button>
              <button onClick={() => setAnzForm(null)}
                className="border border-[#E3DECE] text-[#757067] hover:border-[#BA6D52] px-5 py-2.5 text-[11px] uppercase tracking-[0.15em] transition-colors">
                Abbrechen
              </button>
            </div>
          </div>
        )}

        {anzahlungen.length === 0 && !anzForm && (
          <p className="text-sm text-[#757067] mt-3">
            Noch keine Anzahlung hinterlegt. Bar oder per Überweisung erhaltene Vorauszahlungen
            werden hier als Anzahlungsrechnung belegt und später automatisch verrechnet.
          </p>
        )}
      </div>

      {/* Angebote & Menüs */}
      {(data?.angebote || []).length > 0 && (
        <div data-testid="kunde-angebote" className="border border-[#E3DECE] bg-white p-5 md:p-6">
          <p className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">
            <UtensilsCrossed size={13} /> Angebote & Menüs ({data.angebote.length})
          </p>
          <div className="mt-4 space-y-3">
            {data.angebote.map((a) => (
              <details key={a.id} data-testid={`kunde-angebot-${a.id}`}
                className="border border-[#EFE9DC] bg-[#FAFAF7] group" open={a.status === "angenommen"}>
                <summary className="cursor-pointer list-none px-4 py-3 flex flex-wrap items-baseline justify-between gap-3">
                  <span className="min-w-0">
                    <span className="font-mono text-xs text-[#BA6D52]">{a.nummer || "—"}</span>
                    <span className="text-sm text-[#2C2A26]"> · {a.anlass || "Angebot"}</span>
                    <span className="text-xs text-[#757067]">
                      {a.datum ? ` · ${formatDateDE(a.datum)}` : ""}
                      {a.uhrzeit ? ` ${a.uhrzeit} Uhr` : ""}
                      {a.personen > 0 ? ` · ${a.personen} Personen` : ""}
                    </span>
                  </span>
                  <span className="text-xs whitespace-nowrap">
                    <span className={a.status === "angenommen" ? "text-[#4F6B3E]" : "text-[#757067]"}>
                      {a.status === "angenommen" ? "angenommen" : a.status === "rueckmeldung" ? "Änderungswunsch" : "offen"}
                    </span>
                    <span className="text-[#2C2A26]"> · {num(a.netto_eur)} € netto</span>
                  </span>
                </summary>
                <div className="px-4 pb-4 text-sm">
                  {a.untertitel && <p className="italic text-[#757067]">{a.untertitel}</p>}
                  {a.gaenge.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {a.gaenge.map((g, i) => (
                        <div key={i} className="border-l-2 border-[#E3DECE] pl-3">
                          {g.label && <p className="text-[9px] uppercase tracking-[0.2em] text-[#BA6D52]">{g.label}</p>}
                          <p className="text-[#2C2A26]">{g.titel}</p>
                          {g.beschreibung && <p className="text-xs text-[#757067] italic">{g.beschreibung}</p>}
                          {g.kunden_notiz && (
                            <p className="text-xs text-[#A6543F] mt-1">Wunsch: {g.kunden_notiz}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {a.leistungen.length > 0 && (
                    <ul className="mt-3 text-xs text-[#757067] list-disc pl-5 space-y-0.5">
                      {a.leistungen.map((l, i) => <li key={i}>{l}</li>)}
                    </ul>
                  )}
                  <p className="mt-3 text-xs text-[#757067]">
                    {a.ort ? `${a.ort} · ` : ""}
                    {a.personen > 0 && a.preis_pro_person_netto > 0
                      ? `${num(a.preis_pro_person_netto)} € p. P.` : ""}
                    {a.anfahrt_pauschale_netto > 0 ? ` · ${num(a.anfahrt_pauschale_netto)} € Fahrtkosten` : ""}
                    {a.angenommen_am ? ` · angenommen am ${formatDateDE(a.angenommen_am.slice(0, 10))}` : ""}
                    {a.angenommen_von ? ` von ${a.angenommen_von}` : ""}
                    {a.rechnung_nummer ? ` · Rechnung ${a.rechnung_nummer}` : ""}
                  </p>
                  {a.url && (
                    <a href={a.url} target="_blank" rel="noreferrer"
                      className="mt-2 inline-block text-xs text-[#8A5A42] underline underline-offset-4">
                      Angebotsseite öffnen
                    </a>
                  )}
                </div>
              </details>
            ))}
          </div>
        </div>
      )}

      {/* Offene Rechnungen */}
      {offene.length > 0 && (
        <div data-testid="kunde-offene-rechnungen" className="border border-[#BA6D52] bg-[#FAF5F1] p-5 md:p-6">
          <div className="flex flex-wrap items-baseline justify-between gap-3">
            <p className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">
              <Receipt size={13} /> Offene Rechnungen ({offene.length})
            </p>
            <p className="font-serif text-2xl text-[#BA6D52]" data-testid="kunde-offen-summe">
              {num(s?.offen_brutto_eur)} € offen
            </p>
          </div>
          <div className="mt-4 space-y-2">
            {offene.map((o) => (
              <div key={o.id} data-testid={`kunde-offen-${o.nummer}`}
                className="border border-[#E3DECE] bg-white p-4">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="text-sm">
                    <span className="font-mono text-xs text-[#BA6D52]">{o.nummer}</span>
                    <span className="text-[#757067]">
                      {" · "}{o.art === "direct" ? "Sammelrechnung" : "Rechnung (Angebot)"}
                      {o.faellig_am ? ` · zahlbar bis ${formatDateDE(o.faellig_am)}` : ""}
                    </span>
                    {o.ueberfaellig && (
                      <span className="ml-2 text-[10px] uppercase tracking-[0.15em] text-red-700">überfällig</span>
                    )}
                  </span>
                  <span className="font-serif text-lg text-[#2C2A26]">{num(o.brutto_eur)} € brutto</span>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  <button onClick={() => rechnungAktion(o, "seite")} disabled={!!busyId}
                    data-testid={`kunde-offen-${o.nummer}-seite`}
                    className="inline-flex items-center gap-2 border border-[#E3DECE] hover:border-[#BA6D52] hover:text-[#BA6D52] px-3 py-2 text-[10px] uppercase tracking-[0.15em]">
                    <FileText size={13} /> Rechnungsseite
                  </button>
                  <button onClick={() => rechnungAktion(o, "mail")} disabled={!!busyId}
                    data-testid={`kunde-offen-${o.nummer}-mail`}
                    className="inline-flex items-center gap-2 border border-[#E3DECE] hover:border-[#BA6D52] hover:text-[#BA6D52] px-3 py-2 text-[10px] uppercase tracking-[0.15em]">
                    <Mail size={13} /> Erneut per E-Mail
                  </button>
                  <button onClick={() => rechnungAktion(o, "whatsapp")} disabled={!!busyId}
                    data-testid={`kunde-offen-${o.nummer}-whatsapp`}
                    className="inline-flex items-center gap-2 border border-[#CFE0C3] text-[#25632F] hover:bg-[#F3F7EF] px-3 py-2 text-[10px] uppercase tracking-[0.15em]">
                    <MessageCircle size={13} /> Per WhatsApp
                  </button>
                  <button onClick={() => rechnungAktion(o, "bezahlt")} disabled={!!busyId}
                    data-testid={`kunde-offen-${o.nummer}-bezahlt`}
                    className="inline-flex items-center gap-2 border border-[#BA6D52] bg-[#BA6D52] text-white hover:bg-[#9E5841] px-3 py-2 text-[10px] uppercase tracking-[0.15em]">
                    <CheckCircle2 size={13} /> Zahlung erhalten
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Große Aktionen */}
      <div className={`grid grid-cols-1 gap-3 ${kunde.typ === "privat" ? "md:grid-cols-2" : "md:grid-cols-3"}`}>
        {kunde.typ !== "privat" && (
          <>
            <BigAction icon={Clock} title="Stunden erfassen" testId="kunde-action-stunden"
              hint="Einsatz eintragen und per WhatsApp bestätigen lassen"
              onClick={() => onRecordHours?.(kunde)} />
            <BigAction icon={Receipt} title="Sammelrechnung" testId="kunde-action-sammelrechnung"
              hint={openShifts.length ? `${openShifts.length} bestätigte Einsätze offen` : "Keine offenen Stunden"}
              onClick={alleOffenen} />
          </>
        )}
        {kunde.typ === "privat" && (
          <BigAction icon={UtensilsCrossed} title="Angebotsseite" testId="kunde-action-angebotsseite"
            hint="Menü-Angebot mit Preis und Fahrtkosten erstellen"
            onClick={() => onOpenTab?.("angebotsseiten")} />
        )}
        <BigAction icon={FileSignature} title="Rechnung schreiben" testId="kunde-action-rechnung"
          hint="Direkt-Rechnung mit Kundendaten"
          onClick={leereRechnung} />
      </div>
      <div className="flex flex-wrap gap-2">
        <SmallAction icon={FileText} testId="kunde-action-angebot" onClick={() => onOpenTab?.("quotes")}>
          Angebot / Stundenangebot
        </SmallAction>
        <SmallAction icon={UtensilsCrossed} testId="kunde-action-menuekarte"
          onClick={() => onOpenTab?.("menuekarten", { kundeId: kunde.id, kundeName: kunde.betrieb })}>
          Neue Menükarte
        </SmallAction>
        <SmallAction icon={ClipboardList} testId="kunde-action-kartei" onClick={karteiAnlegen}>
          Kartei anlegen
        </SmallAction>
        {kunde.typ !== "privat" && (
          <SmallAction icon={Clock} testId="kunde-action-nachweise" onClick={() => onOpenTab?.("shifts")}>
            Alle Stundennachweise
          </SmallAction>
        )}
      </div>

      {loading && <div className="py-10 text-center"><Loader2 className="animate-spin text-[#BA6D52] mx-auto" size={24} /></div>}

      {!loading && data && (
        <div className="space-y-8">
          {!(kunde.typ === "privat" && data.shifts.length === 0) && (
          <Block icon={Clock} title="Stundenkonto" count={data.shifts.length}>
            {months.length === 0 ? (
              <p className="text-sm text-[#757067] italic">Noch keine Stundennachweise.</p>
            ) : months.map((g) => (
              <div key={g.ym} data-testid={`kunde-monat-${g.ym}`} className="border border-[#E3DECE] bg-white mb-3">
                <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-[#E3DECE] bg-[#FAFAF7]">
                  <p className="text-sm">
                    <b className="font-serif text-lg">{monthLabel(g.ym)}</b>
                    <span className="text-[#757067]"> · {g.items.length} Einsätze · {num(g.hours)} Std. · {num(g.net)} € netto</span>
                  </p>
                  {billable(g).length > 0 && (
                    <button onClick={() => sammelrechnung(g)} data-testid={`kunde-monat-${g.ym}-invoice`}
                      className="inline-flex items-center gap-2 bg-[#BA6D52] text-white hover:bg-[#9E5841] px-4 py-2 text-[10px] uppercase tracking-[0.15em] transition-colors">
                      <Receipt size={12} /> Sammelrechnung ({billable(g).length})
                    </button>
                  )}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <tbody>
                      {g.items.map((x) => (
                        <tr key={x.id} className="border-b border-[#F2EFE9] last:border-b-0">
                          <td className="p-3">{formatDateDE(x.datum)}</td>
                          <td className="p-3 text-[#757067]">{x.von}–{x.bis}</td>
                          <td className="p-3 text-right">{num(x.hours)} Std.</td>
                          <td className="p-3 text-right">{num(x.net_eur)} €</td>
                          <td className="p-3 text-right text-[10px] uppercase tracking-[0.1em]">
                            {x.invoiced_at ? <span className="text-[#BA6D52]">abgerechnet</span>
                              : x.confirmed_at ? <span className="text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 size={11} /> bestätigt</span>
                              : <span className="text-[#757067]">offen</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </Block>
          )}

          <Block icon={UtensilsCrossed} title="Menükarten" count={menus.length}>
            {menus.length === 0 ? (
              <p className="text-sm text-[#757067] italic">
                Noch keine gespeicherten Menüs für diesen Kunden — über „Neue Menükarte" anlegen und speichern.
              </p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {menus.map((m) => (
                  <button key={m.id} data-testid={`kunde-menue-${m.id}`}
                    onClick={() => onOpenTab?.("menuekarten", { menu: m, kundeId: kunde.id, kundeName: kunde.betrieb })}
                    className="text-left border border-[#E3DECE] bg-white hover:border-[#BA6D52] p-3 transition-colors">
                    <span className="block font-serif text-base text-[#2C2A26]">{m.name}</span>
                    <span className="block text-xs text-[#757067]">
                      {[m.datum, `${m.gaenge?.length || 0} Gänge`].filter(Boolean).join(" · ")} · öffnen und drucken
                    </span>
                  </button>
                ))}
              </div>
            )}
          </Block>

          <Block icon={ClipboardList} title="Veranstaltungs-Karteien" count={karteien.length}>
            {karteien.length === 0 ? (
              <p className="text-sm text-[#757067] italic" data-testid="kunde-karteien-leer">
                Noch keine Kartei für diesen Kunden — über „Kartei anlegen" starten. Einkauf,
                Mitnehmen-Liste und To-dos kannst Du dann aus früheren Karteien übernehmen.
              </p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {karteien.map((k) => {
                  const offen = ["einkauf", "mitnehmen", "todos"]
                    .reduce((a, f) => a + (k[f] || []).filter((p) => !p.erledigt).length, 0);
                  return (
                    <button key={k.id} data-testid={`kunde-kartei-${k.id}`}
                      onClick={() => onOpenTab?.("karteien", { karteiId: k.id })}
                      className="text-left border border-[#E3DECE] bg-white hover:border-[#BA6D52] p-3 transition-colors">
                      <span className="block font-serif text-base text-[#2C2A26]">
                        {k.titel || k.kunde || "Kartei"}
                      </span>
                      <span className="block text-xs text-[#757067]">
                        {[k.datum ? formatDateDE(k.datum) : null,
                          k.personen ? `${k.personen} Personen` : null,
                          `${offen} offen`].filter(Boolean).join(" · ")}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </Block>

          <Block icon={Receipt} title="Rechnungen" count={data.invoices.length}>
            {data.invoices.length === 0 ? (
              <p className="text-sm text-[#757067] italic">Noch keine Rechnungen.</p>
            ) : (
              <div className="space-y-2">
                {data.invoices.map((x) => (
                  <div key={x.id} className={`border border-[#E3DECE] p-3 flex flex-wrap items-center justify-between gap-2 text-sm ${x.status === "cancelled" ? "bg-[#F2EFE9] text-[#757067]" : "bg-white"}`}>
                    <span>
                      <span className={`font-mono text-xs text-[#BA6D52] ${x.status === "cancelled" ? "line-through" : ""}`}>{x.invoice_number || "—"}</span>
                      <span className="text-[#757067]"> · {x.created_at ? new Date(x.created_at).toLocaleDateString("de-DE") : "—"} · {x.positions} Positionen · {x.status === "cancelled" ? "storniert" : x.status === "paid" ? "bezahlt" : x.status === "sent" ? "versendet" : x.status}</span>
                      {x.credit_note_number && (
                        <span className="text-[#757067]"> · Gutschrift {x.credit_note_number}</span>
                      )}
                    </span>
                    <span className={x.status === "cancelled" ? "line-through" : ""}>{num(x.gross_eur)} € brutto</span>
                  </div>
                ))}
              </div>
            )}
          </Block>

          <Block icon={FileText} title="Anfragen & Aufträge" count={data.inquiries.length}>
            {data.inquiries.length === 0 ? (
              <p className="text-sm text-[#757067] italic">Keine Anfragen.</p>
            ) : (
              <div className="space-y-2">
                {data.inquiries.map((x) => (
                  <div key={x.id} className="border border-[#E3DECE] bg-white p-3 flex flex-wrap items-center justify-between gap-2 text-sm">
                    <span>
                      <span className="font-mono text-xs text-[#BA6D52]">{x.confirmation_number || x.invoice_number || "—"}</span>
                      <span className="text-[#757067]"> · {x.einsatzdatum ? formatDateDE(x.einsatzdatum) : "—"} · {x.status}</span>
                    </span>
                    <span className="text-right">
                      {x.net_eur > 0 ? (
                        <>
                          <span className="block">{num(x.net_eur)} € netto</span>
                          <span className="block text-xs text-[#757067]">
                            + {x.mwst_satz} % MwSt = {num(x.gross_eur)} € brutto
                          </span>
                        </>
                      ) : "—"}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Block>
        </div>
      )}
    </div>
  );
}
